import { useCallback, useState } from "react";
import { createElement } from "react";
import type { ChatPdfProps } from "./chatPdf/ChatPdfDocument";

/**
 * Builds the chat PDF from the message data rather than screenshotting the DOM.
 *
 * The previous implementation rasterized #chat-container with html2canvas and
 * pasted the bitmap into A4 pages. That had two failure modes that produced
 * blank PDFs: canvas allocation silently fails past the browser's area limit
 * (~2,300 CSS px of chat on iOS Safari), and html2canvas forces an opaque white
 * backdrop for non-documentElement targets, so dark-mode chats came out
 * white-on-white. Rendering from data removes both, and makes the output
 * selectable, searchable and far smaller.
 *
 * The renderer and its markdown stack are ~700 kB, so they are imported only
 * when the user actually exports, keeping them out of the main bundle.
 */
export const useChatPdf = (filename: string) => {
    const [isGenerating, setIsGenerating] = useState(false);

    const downloadPdf = useCallback(
        async (props: ChatPdfProps) => {
            setIsGenerating(true);
            try {
                const [{ pdf }, { ChatPdfDocument }] = await Promise.all([
                    import("@react-pdf/renderer"),
                    import("./chatPdf/ChatPdfDocument"),
                ]);

                const blob = await pdf(createElement(ChatPdfDocument, props)).toBlob();
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                // Revoke on the next tick so the download has taken the handle.
                setTimeout(() => URL.revokeObjectURL(url), 0);
            } finally {
                setIsGenerating(false);
            }
        },
        [filename]
    );

    return { downloadPdf, isGenerating };
};
